// CursosPorPerfilSection.js — Contenidos agrupados por perfil (subrol).
//
// Cada perfil del proyecto (config_json.subroles) se muestra como un bloque con
// su título ("Cursos para Auditores", "Cursos para Médicos", …) y una grilla de
// tarjetas-módulo: cabecera con "Módulo N" + título del contenido, docente,
// barra de avance y botón "Ingresar".
//
// Qué ve cada quien:
//   • usuario normal  → solo el bloque de SU perfil (users.subrol)
//   • admin/mentor    → todos los bloques (config.admin_ve_todos, por defecto sí)
// Un contenido sin perfiles asignados aparece en todos los bloques (misma regla
// que documentos.php: sin filas en documento_subroles = visible para todos).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BACKEND_BASE, mentor2Service, consultaService, profileService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildBg } from '../bgHelpers';

const Wrapper = styled.section`
  padding: ${p => p.$padding || '2.5rem 0 3.5rem'};
  background: ${p => p.$bg || 'transparent'};
`;
const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1080px'};
  margin: 0 auto;
  padding: 0 1.5rem;
`;
const Header = styled.div`
  text-align: ${p => p.$align || 'left'};
  margin-bottom: 1.5rem;
`;
const Titulo = styled.h2`
  margin: 0;
  font-family: inherit;
  font-weight: 700;
  font-size: ${p => p.$size || '30px'};
  color: ${p => p.$color || '#0f355b'};
`;
const Subtitulo = styled.div`
  margin: 0.4rem 0 0;
  font-size: ${p => p.$size || '1rem'};
  color: ${p => p.$color || '#64748b'};
`;
const Grupo = styled.div`
  & + & { margin-top: ${p => p.$sep || '2.75rem'}; }
`;
const GrupoTitulo = styled.h3`
  margin: 0 0 1.1rem;
  font-family: inherit;
  font-weight: 700;
  font-size: ${p => p.$size || '26px'};
  color: ${p => p.$color || '#0f355b'};
  text-align: ${p => p.$align || 'left'};
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 2}, 1fr);
  gap: ${p => p.$gap || '1.25rem'};
  align-items: start;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const Card = styled.article`
  background: ${p => p.$bg || '#ffffff'};
  border: 1px solid ${p => p.$border || 'rgba(15,53,91,0.10)'};
  border-radius: ${p => p.$radius || '14px'};
  overflow: hidden;
  box-shadow: ${p => p.$sombra || '0 6px 18px rgba(15,53,91,0.08)'};
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const Cabecera = styled.div`
  background: ${p => p.$bg || 'linear-gradient(to right, #29abe2, #1b75bc)'};
  color: ${p => p.$color || '#ffffff'};
  text-align: center;
  padding: ${p => p.$padding || '0.7rem 1rem 0.85rem'};
`;
const CabEtiqueta = styled.div`
  font-weight: 700;
  font-size: ${p => p.$size || '1rem'};
  line-height: 1.2;
`;
const CabTitulo = styled.div`
  margin-top: 3px;
  font-size: ${p => p.$size || '0.85rem'};
  line-height: 1.3;
  font-weight: ${p => p.$peso || '400'};
  span.destacado {
    background: ${p => p.$hlBg || '#ffe600'};
    color: ${p => p.$hlColor || '#0f355b'};
    padding: 0 4px;
    border-radius: 2px;
  }
`;
const Cuerpo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: ${p => p.$padding || '0.95rem 1.05rem 1.05rem'};
  flex: 1;
`;
const Foto = styled.div`
  width: ${p => p.$size || '58px'};
  height: ${p => p.$size || '58px'};
  border-radius: 50%;
  flex-shrink: 0;
  background-image: url(${p => p.$src});
  background-size: cover;
  background-position: center;
  background-color: #e2e8f0;
`;
const Datos = styled.div`
  flex: 1;
  min-width: 0;
  text-align: ${p => p.$align || 'center'};
`;
const Docente = styled.div`
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '0.95rem'};
  color: ${p => p.$color || '#0f355b'};
  line-height: 1.25;
`;
const Barra = styled.div`
  height: ${p => p.$alto || '3px'};
  border-radius: 3px;
  margin: 0.55rem 0 0.35rem;
  background: ${p => p.$bg || '#e2e8f0'};
  overflow: hidden;
`;
const BarraFill = styled.div`
  height: 100%;
  width: ${p => p.$pct || 0}%;
  background: ${p => p.$color || '#29abe2'};
  transition: width 0.4s ease;
`;
const Pct = styled.div`
  font-size: ${p => p.$size || '0.65rem'};
  font-weight: ${p => p.$peso || '700'};
  letter-spacing: 0.4px;
  color: ${p => p.$color || '#64748b'};
  margin-bottom: 0.55rem;
`;
// Los dos botones (ingresar + examen) van en la misma fila. Si solo hay uno,
// ocupa todo el ancho como antes; si no caben, el segundo baja.
const Botones = styled.div`
  display: flex;
  gap: ${p => p.$gap || '0.5rem'};
  flex-wrap: wrap;
  & > * { flex: 1 1 45%; min-width: 0; }
`;
const Boton = styled.button`
  display: block;
  width: 100%;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  letter-spacing: 0.6px;
  font-size: ${p => p.$size || '0.78rem'};
  padding: ${p => p.$padding || '0.55rem 1rem'};
  border-radius: ${p => p.$radius || '6px'};
  color: ${p => p.$color || '#ffffff'};
  background: ${p => p.$bg || '#0f355b'};
  transition: 0.2s;
  &:hover { opacity: 0.92; transform: translateY(-1px); }
`;
// Botón del examen: mismo tamaño que el de ingresar, pero en versión contorno
// para que se lea como una acción distinta. Deshabilitado mientras falten
// lecciones (el motivo va en el title).
const BotonExamen = styled.button`
  display: block;
  width: 100%;
  cursor: ${p => (p.$off ? 'not-allowed' : 'pointer')};
  font-family: inherit;
  font-weight: 700;
  letter-spacing: 0.6px;
  font-size: ${p => p.$size || '0.78rem'};
  padding: ${p => p.$padding || '0.55rem 1rem'};
  border-radius: ${p => p.$radius || '6px'};
  border: 2px solid ${p => (p.$off ? '#cbd5e1' : (p.$color || '#0f355b'))};
  color: ${p => (p.$off ? '#94a3b8' : (p.$color || '#0f355b'))};
  background: ${p => p.$bg || 'transparent'};
  opacity: ${p => (p.$off ? 0.75 : 1)};
  transition: 0.2s;
  &:hover { ${p => (p.$off ? '' : 'opacity: 0.92; transform: translateY(-1px);')} }
`;
const Vacio = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${p => p.$color || '#94a3b8'};
`;

const resolveImg = (img) => (!img ? null : (img.startsWith('http') ? img : `${BACKEND_BASE}/${img}`));

const CursosPorPerfilSection = ({ config = {}, proyecto, openLogin, tl }) => {
  const c = config;
  const navigate = useNavigate();
  const { user } = useAuth();
  const traducir = (v, fallback = '') => (tl ? (tl(v) || fallback) : (v || fallback));

  const documentos = useMemo(() => proyecto?.documentos || [], [proyecto]);
  const subrolesCatalogo = useMemo(
    () => (Array.isArray(proyecto?.config_json?.subroles) ? proyecto.config_json.subroles : []),
    [proyecto]
  );

  const esAdmin = !!user && (user.role === 'admin' || user.role === 'mentor');
  const verTodos = esAdmin && c.admin_ve_todos !== false;

  // --- Perfil del usuario. El objeto de sesión puede venir de un login viejo
  // (sin subrol), así que lo pedimos al perfil cuando falta.
  const [subrolUsuario, setSubrolUsuario] = useState(user?.subrol || '');
  useEffect(() => {
    if (!user || user.subrol !== undefined) { setSubrolUsuario(user?.subrol || ''); return; }
    let vivo = true;
    profileService.getProfile()
      .then(r => { if (vivo) setSubrolUsuario(r.data?.user?.subrol || ''); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user]);

  // --- Contenidos que el usuario puede ver (documentos.php ya filtra por rol,
  // país, idioma, convenio y perfil). El admin ve el catálogo completo.
  const [permitidos, setPermitidos] = useState(null); // null = sin filtro (admin o error)
  const [esperando, setEsperando] = useState(false);   // evita mostrar contenidos que luego se filtran
  useEffect(() => {
    if (!user || verTodos) { setPermitidos(null); setEsperando(false); return; }
    let vivo = true;
    setEsperando(true);
    consultaService.getDocumentos()
      .then(r => { if (vivo) setPermitidos(new Set((r.data || []).map(d => String(d.id)))); })
      .catch(() => { if (vivo) setPermitidos(null); })
      .finally(() => { if (vivo) setEsperando(false); });
    return () => { vivo = false; };
  }, [user, verTodos]);

  // --- Avance del modo mentor por documento.
  const [progresoMap, setProgresoMap] = useState({});
  const [evalMap, setEvalMap] = useState({});         // { docId: {hecha, aprobada} }
  const [leccionesMap, setLeccionesMap] = useState({}); // { docId: {total, hechas} }
  const docIdsKey = documentos.map(d => d.id).join(',');
  // Con `progreso_incluye_examen`, en los cursos que tienen evaluación el examen
  // cuenta como un paso más: el 100% exige aprobarlo.
  const conExamen = c.progreso_incluye_examen === true;
  useEffect(() => {
    if (!user || !docIdsKey) { setProgresoMap({}); setEvalMap({}); setLeccionesMap({}); return; }
    let vivo = true;
    mentor2Service.progresoProyecto(docIdsKey.split(','), { incluirExamen: conExamen })
      .then(res => {
        if (!vivo || !res.data?.success) return;
        setProgresoMap(res.data.progreso || {});
        setEvalMap(res.data.evaluacion || {});
        setLeccionesMap(res.data.lecciones || {});
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, docIdsKey, conExamen]);

  if (documentos.length === 0 || esperando) return null;

  // ---- Perfiles a mostrar ----
  const ocultos = Array.isArray(c.perfiles_ocultos) ? c.perfiles_ocultos.map(String) : [];
  const orden = Array.isArray(c.perfiles_orden) ? c.perfiles_orden.map(String) : [];
  const catalogo = subrolesCatalogo.filter(s => s && s.key && !ocultos.includes(String(s.key)));
  const ordenados = orden.length > 0
    ? [
        ...orden.map(k => catalogo.find(s => String(s.key) === k)).filter(Boolean),
        ...catalogo.filter(s => !orden.includes(String(s.key))),
      ]
    : catalogo;

  const perfilesVisibles = verTodos
    ? ordenados
    : ordenados.filter(s => String(s.key) === String(subrolUsuario));

  // Un usuario sin perfil (o con un perfil que ya no existe) ve un bloque único
  // con lo que tenga permitido, salvo que se configure lo contrario.
  const sinPerfil = perfilesVisibles.length === 0;
  if (sinPerfil && c.ocultar_sin_perfil === true) return null;

  // Orden manual por perfil (config.orden_por_perfil[key] = [ids]). Lo que no
  // esté en la lista queda al final, en el orden que trae el proyecto.
  const ordenarPerfil = (key, docs) => {
    const lista = (c.orden_por_perfil || {})[key];
    if (!Array.isArray(lista) || lista.length === 0) return docs;
    const pos = new Map(lista.map((id, i) => [String(id), i]));
    return [...docs].sort((a, b) => {
      const pa = pos.has(String(a.id)) ? pos.get(String(a.id)) : Number.MAX_SAFE_INTEGER;
      const pb = pos.has(String(b.id)) ? pos.get(String(b.id)) : Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });
  };

  const docsDelPerfil = (key) => documentos.filter(d => {
    const subs = Array.isArray(d.subroles) ? d.subroles.map(String) : [];
    const encaja = subs.length === 0 || subs.includes(String(key));
    if (!encaja) return false;
    if (permitidos && !permitidos.has(String(d.id))) return false;
    return true;
  });
  const docsSinPerfil = () => documentos.filter(d => !permitidos || permitidos.has(String(d.id)));

  const bloques = sinPerfil
    ? [{ key: '', titulo: traducir(c.titulo_sin_perfil, ''), docs: docsSinPerfil() }]
    : perfilesVisibles.map(s => ({
        key: s.key,
        titulo: traducir((c.titulos_grupo || {})[s.key], '')
          || `${c.prefijo_grupo != null ? c.prefijo_grupo : 'Cursos para '}${s.label || s.key}`,
        docs: ordenarPerfil(s.key, docsDelPerfil(s.key)),
      }));

  // Los bloques sin contenidos se ocultan al participante (salvo que se pida lo
  // contrario), pero el admin siempre ve la estructura completa de perfiles.
  const mostrarVacios = verTodos || c.mostrar_grupo_vacio === true;
  const bloquesVisibles = mostrarVacios ? bloques : bloques.filter(b => b.docs.length > 0);

  const modos = proyecto?.config_json?.modos_habilitados || {};
  const destacados = (Array.isArray(c.docs_destacados) ? c.docs_destacados : []).map(String);
  // `traducir`: el texto puede venir traducido ({es, pt}); vacío a propósito = sin etiqueta.
  const etiqueta = c.etiqueta_modulo != null ? traducir(c.etiqueta_modulo) : 'Módulo';
  const textoBoton = traducir(c.texto_boton, '') || 'INGRESAR';
  const textoPct = c.texto_completado != null ? c.texto_completado : '% COMPLETADO';
  const cabeceraBg = c.color_cabecera_2
    ? `linear-gradient(${c.direccion_cabecera || 'to right'}, ${c.color_cabecera || '#29abe2'}, ${c.color_cabecera_2})`
    : (c.color_cabecera || 'linear-gradient(to right, #29abe2, #1b75bc)');

  const destino = (d) => {
    const modo = c.destino_boton || 'auto';
    if (modo === 'consulta') return `/consulta/${d.id}`;
    if (modo === 'evaluacion') return `/evaluacion/${d.id}`;
    if (modo === 'mentor') return `/mentor/${d.id}`;
    if (Number(d.modo_mentor) === 1 && modos.mentor !== false) return `/mentor/${d.id}`;
    if (Number(d.modo_evaluacion) === 1 && modos.evaluacion !== false) return `/evaluacion/${d.id}`;
    return `/consulta/${d.id}`;
  };
  const ir = (d) => {
    if (user) navigate(destino(d));
    else if (openLogin) openLogin();
    else navigate('/login');
  };

  const pctDoc = (d) => {
    const p = progresoMap[String(d.id)];
    return Math.round(Number(p != null ? p : (d.progreso || 0)) || 0);
  };

  // ---- Examen del contenido ----
  // Opt-in (`mostrar_boton_examen`) para no cambiar las secciones ya publicadas
  // de otros proyectos. Solo aparece en los contenidos con evaluación activa.
  const mostrarExamen = c.mostrar_boton_examen === true;
  const textoExamen = traducir(c.texto_boton_examen, '') || 'EXAMEN';
  const textoExamenHecho = traducir(c.texto_boton_examen_aprobado, '') || 'EXAMEN APROBADO';
  const tieneExamen = (d) => Number(d.modo_evaluacion) === 1 && modos.evaluacion !== false;
  const leccionesDoc = (d) => leccionesMap[String(d.id)] || null;
  // Faltan lecciones = las que el backend contó como no completadas. Se mira
  // aquí y no en el porcentaje porque el porcentaje ya puede incluir el examen.
  const faltanLecciones = (d) => {
    const l = leccionesDoc(d);
    if (!l || !l.total) return false;
    return Number(l.hechas) < Number(l.total);
  };
  const examenAprobado = (d) => !!(evalMap[String(d.id)] && evalMap[String(d.id)].aprobada);
  const examenBloqueado = (d) => c.examen_requiere_lecciones !== false && faltanLecciones(d);
  const motivoExamen = (d) => {
    if (!examenBloqueado(d)) return '';
    const l = leccionesDoc(d);
    const pendientes = l ? Number(l.total) - Number(l.hechas) : 0;
    const plantilla = traducir(c.texto_examen_bloqueado, '')
      || 'Termina las lecciones del contenido para presentar el examen';
    return pendientes > 0
      ? `${plantilla} (te falta${pendientes === 1 ? '' : 'n'} ${pendientes})`
      : plantilla;
  };
  const docenteDe = (d) => {
    const sel = Array.isArray(d.docentes_sel) ? d.docentes_sel : [];
    if (sel.length > 0) return { nombre: sel[0].nombre, foto: sel[0].foto };
    const mod = (d.modulos || []).find(m => m.docente_nombre);
    if (mod) return { nombre: mod.docente_nombre, foto: mod.docente_foto };
    if (d.docente) return { nombre: d.docente, foto: d.docente_foto };
    return null;
  };

  return (
    <Wrapper $bg={buildBg(c, 'transparent')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        {(c.titulo || c.subtitulo) && (
          <Header $align={c.alineacion_titulo || 'left'}>
            {c.titulo && (
              <Titulo $size={c.tamano_titulo} $color={c.color_titulo}
                dangerouslySetInnerHTML={{ __html: traducir(c.titulo) }} />
            )}
            {c.subtitulo && (
              <Subtitulo $size={c.tamano_subtitulo} $color={c.color_subtitulo}
                dangerouslySetInnerHTML={{ __html: traducir(c.subtitulo) }} />
            )}
          </Header>
        )}

        {bloquesVisibles.length === 0 ? (
          <Vacio $color={c.color_texto_vacio}>
            {traducir(c.texto_vacio, '') || 'Todavía no hay contenidos disponibles para tu perfil.'}
          </Vacio>
        ) : bloquesVisibles.map(b => (
          <Grupo key={b.key || '_sin_perfil'} $sep={c.separacion_grupos}>
            {b.titulo && (
              <GrupoTitulo $size={c.tamano_titulo_grupo} $color={c.color_titulo_grupo} $align={c.alineacion_titulo_grupo}>
                {b.titulo}
              </GrupoTitulo>
            )}
            {b.docs.length === 0 ? (
              <Vacio $color={c.color_texto_vacio}>
                {traducir(c.texto_grupo_vacio, '') || 'Sin contenidos asignados a este perfil.'}
              </Vacio>
            ) : (
              <Grid $cols={parseInt(c.columnas) || 2} $gap={c.gap}>
                {b.docs.map((d, i) => {
                  const doc = docenteDe(d);
                  const pct = pctDoc(d);
                  const esDestacado = destacados.includes(String(d.id));
                  return (
                    <Card key={d.id} $bg={c.color_tarjeta} $border={c.color_borde_tarjeta}
                      $radius={c.radio_tarjeta} $sombra={c.sombra_tarjeta}>
                      <Cabecera $bg={cabeceraBg} $color={c.color_texto_cabecera} $padding={c.padding_cabecera}>
                        {c.mostrar_numero_modulo !== false && (
                          <CabEtiqueta $size={c.tamano_etiqueta_modulo}>
                            {`${etiqueta} ${i + 1}`.trim()}
                          </CabEtiqueta>
                        )}
                        {c.mostrar_titulo_doc !== false && (
                          <CabTitulo $size={c.tamano_titulo_doc} $peso={c.peso_titulo_doc}
                            $hlBg={c.color_destacado_fondo} $hlColor={c.color_destacado_texto}>
                            {esDestacado
                              ? <span className="destacado">{traducir(d.titulo)}</span>
                              : traducir(d.titulo)}
                          </CabTitulo>
                        )}
                      </Cabecera>
                      <Cuerpo $padding={c.padding_cuerpo}>
                        {c.mostrar_docente !== false && doc?.foto && (
                          <Foto $src={resolveImg(doc.foto)} $size={c.tamano_foto} />
                        )}
                        <Datos $align={c.alineacion_datos}>
                          {c.mostrar_docente !== false && doc?.nombre && (
                            <Docente $size={c.tamano_docente} $peso={c.peso_docente} $color={c.color_docente}>{doc.nombre}</Docente>
                          )}
                          {c.mostrar_progreso !== false && (
                            <>
                              <Barra $bg={c.color_barra_fondo} $alto={c.alto_barra}>
                                <BarraFill $pct={pct} $color={c.color_barra} />
                              </Barra>
                              <Pct $size={c.tamano_pct} $peso={c.peso_pct} $color={c.color_pct}>{`${pct}${textoPct}`}</Pct>
                            </>
                          )}
                          <Botones $gap={c.separacion_botones}>
                          {c.mostrar_boton !== false && (
                            <Boton onClick={() => ir(d)} $bg={c.color_boton} $color={c.color_texto_boton}
                              $radius={c.radio_boton} $size={c.tamano_boton} $padding={c.padding_boton}>
                              {textoBoton}
                            </Boton>
                          )}
                          {mostrarExamen && tieneExamen(d) && (
                            <BotonExamen
                              type="button"
                              disabled={examenBloqueado(d)}
                              $off={examenBloqueado(d)}
                              title={motivoExamen(d)}
                              $color={c.color_boton_examen || c.color_boton}
                              $bg={c.color_fondo_boton_examen}
                              $radius={c.radio_boton} $size={c.tamano_boton} $padding={c.padding_boton}
                              onClick={() => {
                                if (examenBloqueado(d)) return;
                                if (user) navigate(`/evaluacion/${d.id}`);
                                else if (openLogin) openLogin();
                                else navigate('/login');
                              }}
                            >
                              {examenAprobado(d) ? `✓ ${textoExamenHecho}` : textoExamen}
                            </BotonExamen>
                          )}
                          </Botones>
                        </Datos>
                      </Cuerpo>
                    </Card>
                  );
                })}
              </Grid>
            )}
          </Grupo>
        ))}
      </Inner>
    </Wrapper>
  );
};

export default CursosPorPerfilSection;
